import { useMutation } from '@hooks/reactQuery';

import { assetCartRequestFullView } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';

import { deleteAssetsFromCart } from '@api/assetCartRequest';

import { handleErrorFromMessage } from '@utils/handleRequestError';
import { endOfDayInUTC, startOfDayInUTC } from '@utils/kpi-utils/date';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IResponseError } from '@interfaces/errors/apiErrors';
import { IAssetCartRequestDTO, ICountNotDeleted } from '@interfaces/rent/dto/assetCartDTO';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';

import {
	ASSET_CART_REQUEST_DELETE,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

import { useInvalidateAcquiredAssets } from './useInvalidateAcquiredAssets';

interface IUseMutateDeleteAssetsFromCartProps {
	closeDeleteConfirm?: () => void;
}

export const useMutateDeleteAssetsFromCart = (props?: IUseMutateDeleteAssetsFromCartProps) => {
	const dispatch = useAppDispatch();

	const {
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		assetOwnerId,
		assetTypeId,
		assetSubTypeId,
		isAvailableInPeriod,
		fromDate,
		toDate,
	} = useAppSelector(assetCartRequestFullView);

	const { invalidateAcquiredAssets } = useInvalidateAcquiredAssets();

	const { closeDeleteConfirm } = props || {};

	const {
		mutate: handleDeleteAssets,
		isLoading: isLoadingDeleteAssets,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [
			ASSET_CART_REQUEST_DELETE,
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			assetOwnerId,
			assetTypeId,
			assetSubTypeId,
			isAvailableInPeriod,
			fromDate,
			toDate,
		],
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const filter: IAssetCartRequestDTO = {
				page,
				pageSize,
				sortDirection,
				sortFieldName,
				searchValue,
				assetOwnerId,
				assetTypeId,
				assetSubTypeId,
				isAvailableInPeriod,
				rentFrom: fromDate && startOfDayInUTC(new Date(fromDate)),
				rentTo: toDate && endOfDayInUTC(new Date(toDate)),
			};

			return deleteAssetsFromCart({
				filter: (actionType === EMenuActionsTypes.allFiltered) && filter
					? filter
					: null,
				ids,
				isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
			});
		},
		onSuccess: ({ countNotDeleted }: ICountNotDeleted) => {
			if (closeDeleteConfirm) closeDeleteConfirm();

			dispatch(setSnackbar({
				message: !countNotDeleted
					? responseMessages.removedAssetsRequestsList
					: `${countNotDeleted} Acquire Assets have not been deleted from the Requests List!`,
				type: !countNotDeleted ? ESnackbarTypes.success : ESnackbarTypes.warning,
			}));

			return invalidateAcquiredAssets();
		},
		onError: (err: IResponseError) => handleErrorFromMessage(err),
	});

	return {
		handleDeleteAssets,
		isLoadingDeleteAssets,
	};
};
